import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Keyboard, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { containersApi, formatError } from "../lib/api";
import { extractQrCode } from "../lib/format";

const QR_ID = "qfresh-camera-region";

export const QRScannerModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const lookingRef = useRef(false); // guards against double-fire while async lookup is in-flight
  const [mode, setMode] = useState("camera"); // camera | manual
  const [manual, setManual] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const stopCamera = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      await s.clear();
    } catch (err) {
      // Stop/clear can throw if the scanner was already torn down; log and move on
      console.warn("QR scanner cleanup warning:", err);
    }
  }, []);

  const handleDetected = useCallback(
    async (text) => {
      if (lookingRef.current) return;
      lookingRef.current = true;
      setBusy(true);
      try {
        // Extract the bare QF code CLIENT-SIDE so we only send safe path
        // characters to the backend — no percent-encoding needed, no risk
        // of double-encoding through Cloudflare/ingress.
        const raw = String(text || "").trim();
        const code = extractQrCode(raw);
        if (!code) {
          setError("That doesn't look like a QFresh code");
          lookingRef.current = false;
          setBusy(false);
          return;
        }
        const result = await containersApi.byQr(code);
        await stopCamera();

        if (result.status === "foreign") {
          // Not a QFresh code at all — stay in the modal and show a soft error
          setError("That doesn't look like a QFresh code");
          lookingRef.current = false;
          setBusy(false);
          return;
        }

        onClose?.();

        if (result.status === "not_found") {
          navigate(`/add?qr=${encodeURIComponent(result.qr_code)}`);
          return;
        }
        if (result.status === "empty_box") {
          // Box exists in your household but is empty (last item consumed/discarded).
          // Send the user straight to Add Container so they can log fresh contents
          // for the same box.
          const params = new URLSearchParams({ qr: result.qr_code });
          if (result.box_number) params.set("box", String(result.box_number));
          if (result.last_item?.name) params.set("last", result.last_item.name);
          if (result.last_item?.status) params.set("last_status", result.last_item.status);
          navigate(`/add?${params.toString()}`);
          return;
        }
        if (result.status === "locked") {
          navigate(`/locked/${encodeURIComponent(result.qr_code)}`, {
            state: {
              owner_name: result.owner_name,
              household_name: result.household_name,
            },
          });
          return;
        }
        // status === "ok"
        navigate(`/container/${result.container.id}`);
      } catch (err) {
        console.warn("QR lookup failed:", err);
        setError(`Couldn't process "${text}"`);
        toast.error(formatError(err));
      } finally {
        lookingRef.current = false;
        setBusy(false);
      }
    },
    [navigate, onClose, stopCamera]
  );

  // Start camera when modal opens in camera mode
  useEffect(() => {
    if (!open || mode !== "camera") return;
    let cancelled = false;

    const start = async () => {
      try {
        const s = new Html5Qrcode(QR_ID, { verbose: false });
        scannerRef.current = s;
        await s.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (cancelled) return;
            handleDetected(decoded);
          },
          () => { /* per-frame decode errors are noisy; intentionally ignored */ }
        );
      } catch (err) {
        console.error("Camera failed to start:", err);
        setError("Camera unavailable. Try entering the code manually.");
      }
    };
    start();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, mode, handleDetected, stopCamera]);

  // Reset state whenever the modal closes
  useEffect(() => {
    if (open) return;
    stopCamera();
    setError("");
    setManual("");
    setMode("camera");
  }, [open, stopCamera]);

  const submitManual = (e) => {
    e?.preventDefault();
    if (!manual.trim()) return;
    handleDetected(manual);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="qr-scanner-modal"
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-5 sm:m-4"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg brand-gradient-bg flex items-center justify-center">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#277A82]">Scan</div>
                  <div className="font-heading text-lg font-semibold text-[#1F5566]">Find a container</div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="close-scanner-btn"
                className="text-[#8A93A6] hover:text-[#1F5566] p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {mode === "camera" ? (
              <div>
                <div
                  id={QR_ID}
                  data-testid="camera-region"
                  className="w-full aspect-square bg-black rounded-2xl overflow-hidden relative"
                />
                {error && <p className="text-sm text-[#9C3E1D] mt-3 text-center">{error}</p>}
                <p className="text-xs text-[#8A93A6] text-center mt-3">
                  Point your camera at a QFresh sticker
                </p>
                <Button
                  onClick={() => setMode("manual")}
                  data-testid="switch-manual-btn"
                  variant="outline"
                  className="w-full mt-4 border-[#E2E0D8] text-[#2D3142]"
                >
                  <Keyboard className="w-4 h-4 mr-2" /> Enter code manually
                </Button>
              </div>
            ) : (
              <form onSubmit={submitManual} className="space-y-3">
                <Input
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="QR-XXXXXXXX"
                  data-testid="manual-input"
                  className="bg-white border-[#E2E0D8] h-12 font-mono uppercase tracking-wider"
                  autoFocus
                />
                {error && <p className="text-sm text-[#9C3E1D]">{error}</p>}
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="manual-lookup-btn"
                  className="w-full brand-gradient-bg text-white h-12 hover:opacity-90"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {busy ? "Looking up…" : "Look up"}
                </Button>
                <Button
                  type="button"
                  onClick={() => setMode("camera")}
                  variant="ghost"
                  className="w-full"
                >
                  <Camera className="w-4 h-4 mr-2" /> Use camera instead
                </Button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
